/* Lightweight per-route SEO manager — sets <title>, meta description,
   canonical URL, OG/Twitter tags, and an optional JSON-LD script tag
   imperatively on document.head. No external dependency.

   Usage:
     useSeo({
       title: "IT jobs in Mauritius",
       description: "Browse all IT jobs in Mauritius — apply with or without CV.",
       canonical: "/jobs/category/it",
       jsonLd: { ... }                       // optional, single object or array
     });
*/
import { useEffect } from "react";

const SITE = "https://motravay.mu";
const DEFAULTS = {
  title: "Find Jobs in Mauritius — Motravay",
  description:
    "Motravay — The #1 job board in Mauritius. Find jobs in Port-Louis, Ebène, Curepipe, Quatre-Bornes, Rose-Hill, Grand Baie. Apply online with or without CV.",
  image: `${SITE}/motravay-logo.png`,
};

const setOrCreateMeta = (selector, attr, value, content) => {
  if (!content && content !== "") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const TAG_ID = "motravay-route-jsonld";
const setJsonLd = (data) => {
  // Remove the previous tag(s) we own
  document.head.querySelectorAll(`script[data-mv="${TAG_ID}"]`).forEach((n) => n.remove());
  if (!data) return;
  const arr = Array.isArray(data) ? data : [data];
  arr.forEach((obj) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-mv", TAG_ID);
    try { s.textContent = JSON.stringify(obj); } catch (_) { s.textContent = "{}"; }
    document.head.appendChild(s);
  });
};

export function useSeo({ title, description, canonical, image, jsonLd, type } = {}) {
  useEffect(() => {
    const t = title || DEFAULTS.title;
    const d = description || DEFAULTS.description;
    const i = image || DEFAULTS.image;
    const cUrl = canonical ? (canonical.startsWith("http") ? canonical : `${SITE}${canonical.startsWith("/") ? "" : "/"}${canonical}`) : `${SITE}${window.location.pathname}`;

    document.title = t;
    setOrCreateMeta('meta[name="description"]:not([lang])', "name", "description", d);
    setLink("canonical", cUrl);

    // Open Graph
    setOrCreateMeta('meta[property="og:title"]', "property", "og:title", t);
    setOrCreateMeta('meta[property="og:description"]', "property", "og:description", d);
    setOrCreateMeta('meta[property="og:image"]', "property", "og:image", i);
    setOrCreateMeta('meta[property="og:url"]', "property", "og:url", cUrl);
    setOrCreateMeta('meta[property="og:type"]', "property", "og:type", type || "website");

    // Twitter
    setOrCreateMeta('meta[name="twitter:title"]', "name", "twitter:title", t);
    setOrCreateMeta('meta[name="twitter:description"]', "name", "twitter:description", d);
    setOrCreateMeta('meta[name="twitter:image"]', "name", "twitter:image", i);

    setJsonLd(jsonLd);

    return () => {
      // Cleanup: remove route-specific JSON-LD so the next route starts clean
      setJsonLd(null);
    };
  }, [title, description, canonical, image, type, JSON.stringify(jsonLd || {})]);
}

export const SITE_URL = SITE;
